import React, { useState, useEffect, useCallback } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Divider,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import TripOriginIcon from '@mui/icons-material/TripOrigin';
import GoogleIcon from '@mui/icons-material/Google';
import CloseIcon from '@mui/icons-material/Close';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { getConfigurationEmbeddedRootSx, isConfigurationMobileLayout } from '../utils/configurationMobileLayout';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { getHotelBasicDetails, saveHotelBasicDetails } from '../services/api';
import HotelLatLngAssist from '../components/configuration/HotelLatLngAssist';
import PageToastStack from '../components/common/PageToastStack';

const BasicDetailsPage = () => {
  const isMobileLayout = isConfigurationMobileLayout();
  // Initialize theme synchronously to prevent flash
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  const isDark = currentTheme === 'dark';

  // Parse time from API (HH:mm or HH:mm:ss) into moment; fallback to default (match backend defaults)
  const parseTimeFromApi = (value, defaultTime) => {
    if (value == null || value === '') return defaultTime;
    const m = moment(value, ['HH:mm', 'HH:mm:ss', 'H:mm'], true);
    return m.isValid() ? m : defaultTime;
  };
  const defaultCheckIn = moment('12:00', 'HH:mm');
  const defaultCheckOut = moment('11:00', 'HH:mm');

  // Form state
  const [formData, setFormData] = useState({
    propertyName: '',
    description: '',
    address: '',
    city: '',
    pincode: '',
    latitude: '',
    longitude: '',
    checkInTime: defaultCheckIn,
    checkOutTime: defaultCheckOut,
    is24x7: false,
    whatsappNumber: '',
    emailAddresses: [],
    mobileNumbers: [],
    landlineNumber: '',
    facebookLink: '',
    linkedinLink: '',
    twitterLink: '',
    instagramLink: '',
    tripadvisorLink: '',
    googleLink: '',
  });

  const [emailInput, setEmailInput] = useState('');
  const [mobileInput, setMobileInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [pageToasts, setPageToasts] = useState([]);

  const pushPageToast = useCallback((message, severity = 'success') => {
    if (message == null || message === '') return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const createdAt = Date.now();
    setPageToasts((prev) => [...prev, { id, message: String(message), severity, createdAt }]);
    window.setTimeout(() => {
      setPageToasts((prev) => prev.filter((t) => t.id !== id));
    }, 7000);
  }, []);

  const dismissPageToast = useCallback((id) => {
    setPageToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Load hotel data from API
  useEffect(() => {
    const loadHotelData = async () => {
      setLoading(true);
      try {
        const data = await getHotelBasicDetails();
        setFormData(prev => ({
          ...prev,
          propertyName: data.name || '',
          description: data.description || '',
          address: data.address || '',
          city: data.city || '',
          pincode: data.pincode || '',
          latitude: data.latitude != null && data.latitude !== '' ? String(data.latitude) : '',
          longitude: data.longitude != null && data.longitude !== '' ? String(data.longitude) : '',
          whatsappNumber: data.whatsapp_number || '',
          emailAddresses: data.email_addresses || [],
          mobileNumbers: data.mobile_numbers || [],
          landlineNumber: data.landline_number || '',
          facebookLink: data.facebook_link || '',
          linkedinLink: data.linkedin_link || '',
          twitterLink: data.twitter_link || '',
          instagramLink: data.instagram_link || '',
          tripadvisorLink: data.tripadvisor_link || '',
          googleLink: data.google_link || '',
          checkInTime: parseTimeFromApi(data.check_in_time, defaultCheckIn),
          checkOutTime: parseTimeFromApi(data.check_out_time, defaultCheckOut),
          is24x7: data.is_24x7 || false,
        }));
      } catch (error) {
        pushPageToast('Failed to load hotel data. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadHotelData();
  }, [pushPageToast]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateURL = (url) => {
    if (!url) return true; // Empty URLs are allowed
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim();
    if (!trimmedEmail) {
      setErrors(prev => ({ ...prev, emailInput: 'Email is required' }));
      return;
    }
    if (!validateEmail(trimmedEmail)) {
      setErrors(prev => ({ ...prev, emailInput: 'Invalid email format' }));
      return;
    }
    if (formData.emailAddresses.includes(trimmedEmail)) {
      setErrors(prev => ({ ...prev, emailInput: 'Email already added' }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      emailAddresses: [...prev.emailAddresses, trimmedEmail],
    }));
    setEmailInput('');
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.emailInput;
      return newErrors;
    });
  };

  const handleRemoveEmail = (email) => {
    setFormData(prev => ({
      ...prev,
      emailAddresses: prev.emailAddresses.filter(e => e !== email),
    }));
  };

  const handleAddMobile = () => {
    const trimmedMobile = mobileInput.trim();
    if (!trimmedMobile) {
      setErrors(prev => ({ ...prev, mobileInput: 'Mobile number is required' }));
      return;
    }
    if (trimmedMobile.length > 20) {
      setErrors(prev => ({ ...prev, mobileInput: 'Mobile number must be 20 characters or less' }));
      return;
    }
    if (formData.mobileNumbers.includes(trimmedMobile)) {
      setErrors(prev => ({ ...prev, mobileInput: 'Mobile number already added' }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      mobileNumbers: [...prev.mobileNumbers, trimmedMobile],
    }));
    setMobileInput('');
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.mobileInput;
      return newErrors;
    });
  };

  const handleRemoveMobile = (mobile) => {
    setFormData(prev => ({
      ...prev,
      mobileNumbers: prev.mobileNumbers.filter(m => m !== mobile),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate property name
    if (!formData.propertyName.trim()) {
      newErrors.propertyName = 'Property name is required';
    } else if (formData.propertyName.length > 255) {
      newErrors.propertyName = 'Property name must be 255 characters or less';
    }

    // Validate check-in time
    if (!formData.checkInTime || !moment.isMoment(formData.checkInTime)) {
      newErrors.checkInTime = 'Check-in time is required';
    }

    // Validate check-out time
    if (!formData.checkOutTime || !moment.isMoment(formData.checkOutTime)) {
      newErrors.checkOutTime = 'Check-out time is required';
    }

    if (formData.address && formData.address.length > 1000) {
      newErrors.address = 'Address must be 1000 characters or less';
    }
    if (formData.city && formData.city.length > 100) {
      newErrors.city = 'City must be 100 characters or less';
    }
    if (formData.pincode && formData.pincode.length > 20) {
      newErrors.pincode = 'Pincode must be 20 characters or less';
    }

    const latTrim = (formData.latitude || '').trim();
    const lngTrim = (formData.longitude || '').trim();
    if (!latTrim && !lngTrim) {
      // no coordinates — allowed
    } else if (!latTrim || !lngTrim) {
      newErrors.latitude = 'Enter both latitude and longitude, or leave both blank.';
    } else {
      const la = Number(latTrim);
      const lo = Number(lngTrim);
      if (!Number.isFinite(la) || !Number.isFinite(lo)) {
        newErrors.latitude = 'Latitude and longitude must be valid numbers.';
      } else {
        if (la < -90 || la > 90) {
          newErrors.latitude = 'Latitude must be between -90 and 90.';
        }
        if (lo < -180 || lo > 180) {
          newErrors.longitude = 'Longitude must be between -180 and 180.';
        }
      }
    }

    // Validate WhatsApp number
    if (formData.whatsappNumber && formData.whatsappNumber.length > 20) {
      newErrors.whatsappNumber = 'WhatsApp number must be 20 characters or less';
    }

    // Validate landline number
    if (formData.landlineNumber && formData.landlineNumber.length > 20) {
      newErrors.landlineNumber = 'Landline number must be 20 characters or less';
    }

    // Validate URLs
    if (formData.facebookLink && !validateURL(formData.facebookLink)) {
      newErrors.facebookLink = 'Invalid Facebook URL';
    }
    if (formData.linkedinLink && !validateURL(formData.linkedinLink)) {
      newErrors.linkedinLink = 'Invalid LinkedIn URL';
    }
    if (formData.twitterLink && !validateURL(formData.twitterLink)) {
      newErrors.twitterLink = 'Invalid Twitter URL';
    }
    if (formData.instagramLink && !validateURL(formData.instagramLink)) {
      newErrors.instagramLink = 'Invalid Instagram URL';
    }
    if (formData.tripadvisorLink && !validateURL(formData.tripadvisorLink)) {
      newErrors.tripadvisorLink = 'Invalid Tripadvisor URL';
    }
    if (formData.googleLink && !validateURL(formData.googleLink)) {
      newErrors.googleLink = 'Invalid Google URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      pushPageToast('Please fix the validation errors before saving.', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.propertyName.trim(),
        description: formData.description.trim() || null,
        address: formData.address.trim() || '',
        city: formData.city.trim() || '',
        pincode: formData.pincode.trim() || '',
        latitude:
          formData.latitude.trim() === '' && formData.longitude.trim() === ''
            ? null
            : Number(formData.latitude.trim()),
        longitude:
          formData.latitude.trim() === '' && formData.longitude.trim() === ''
            ? null
            : Number(formData.longitude.trim()),
        check_in_time: formData.checkInTime ? formData.checkInTime.format('HH:mm') : null,
        check_out_time: formData.checkOutTime ? formData.checkOutTime.format('HH:mm') : null,
        is_24x7: formData.is24x7,
        whatsapp_number: formData.whatsappNumber.trim() || null,
        email_addresses: formData.emailAddresses,
        mobile_numbers: formData.mobileNumbers,
        landline_number: formData.landlineNumber.trim() || null,
        facebook_link: formData.facebookLink.trim() || null,
        linkedin_link: formData.linkedinLink.trim() || null,
        twitter_link: formData.twitterLink.trim() || null,
        instagram_link: formData.instagramLink.trim() || null,
        tripadvisor_link: formData.tripadvisorLink.trim() || null,
        google_link: formData.googleLink.trim() || null,
      };

      const response = await saveHotelBasicDetails(payload);
      pushPageToast(
        response.message || 'Hotel basic details saved successfully!',
        'success',
      );
      setErrors({});
    } catch (error) {
      if (error.errors) {
        setErrors(error.errors);
        pushPageToast('Please fix the validation errors.', 'error');
      } else {
        pushPageToast(error.error || 'Failed to save hotel data. Please try again.', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reload data from API to reset form
    const loadHotelData = async () => {
      setLoading(true);
      try {
        const data = await getHotelBasicDetails();
        setFormData(prev => ({
          ...prev,
          propertyName: data.name || '',
          description: data.description || '',
          address: data.address || '',
          city: data.city || '',
          pincode: data.pincode || '',
          latitude: data.latitude != null && data.latitude !== '' ? String(data.latitude) : '',
          longitude: data.longitude != null && data.longitude !== '' ? String(data.longitude) : '',
          whatsappNumber: data.whatsapp_number || '',
          emailAddresses: data.email_addresses || [],
          mobileNumbers: data.mobile_numbers || [],
          landlineNumber: data.landline_number || '',
          facebookLink: data.facebook_link || '',
          linkedinLink: data.linkedin_link || '',
          twitterLink: data.twitter_link || '',
          instagramLink: data.instagram_link || '',
          tripadvisorLink: data.tripadvisor_link || '',
          googleLink: data.google_link || '',
          checkInTime: parseTimeFromApi(data.check_in_time, defaultCheckIn),
          checkOutTime: parseTimeFromApi(data.check_out_time, defaultCheckOut),
          is24x7: data.is_24x7 || false,
        }));
        setErrors({});
      } catch (error) {} finally {
        setLoading(false);
      }
    };
    loadHotelData();
  };

  const mapQueryAddress = [formData.address, formData.city, formData.pincode]
    .map((value) => (value || '').trim())
    .filter(Boolean)
    .join(', ');
  const coordsFromForm = (() => {
    const t1 = (formData.latitude || '').trim();
    const t2 = (formData.longitude || '').trim();
    if (!t1 || !t2) return null;
    const la = Number(t1);
    const lo = Number(t2);
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return null;
    if (Math.abs(la) > 90 || Math.abs(lo) > 180) return null;
    return { lat: la, lng: lo };
  })();
  const mapEmbedUrl = coordsFromForm
    ? `https://maps.google.com/maps?q=${coordsFromForm.lat},${coordsFromForm.lng}&z=16&output=embed`
    : mapQueryAddress
      ? `https://maps.google.com/maps?q=${encodeURIComponent(mapQueryAddress)}&z=15&output=embed`
      : '';
  const mapOpenUrl = coordsFromForm
    ? `https://www.google.com/maps/search/?api=1&query=${coordsFromForm.lat},${coordsFromForm.lng}`
    : mapQueryAddress
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQueryAddress)}`
      : '';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box>
        <PageToastStack toasts={pageToasts} onDismiss={dismissPageToast} />
        <Grid container spacing={3} sx={{ overflow: 'visible' }}>
          {/* Left Column - Form Fields */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                overflow: 'visible',
                // Room for outlined InputLabel when shrunk (avoids top glyph clipping)
                pt: 0.5,
              }}
            >
              {/* Property Name */}
              <Card elevation={0} sx={{ backgroundColor: 'transparent', boxShadow: 'none', overflow: 'visible' }}>
                <CardContent sx={{ p: 0, overflow: 'visible', '&:last-child': { pb: 0 } }}>
                  <TextField
                    label="Property name"
                    value={formData.propertyName}
                    onChange={(e) => {
                      handleInputChange('propertyName', e.target.value);
                      if (errors.propertyName) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.propertyName;
                          return newErrors;
                        });
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    required
                    error={!!errors.propertyName}
                    helperText={errors.propertyName}
                    sx={{
                      overflow: 'visible',
                      '& .MuiOutlinedInput-root': {
                        overflow: 'visible',
                        backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        overflow: 'visible',
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#4ce1b6',
                      },
                    }}
                  />
                </CardContent>
              </Card>

              {/* Property Description */}
              <Card elevation={0} sx={{ backgroundColor: 'transparent', boxShadow: 'none', overflow: 'visible' }}>
                <CardContent sx={{ p: 0, overflow: 'visible', '&:last-child': { pb: 0 } }}>
                  <TextField
                    label="Property Description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    fullWidth
                    multiline
                    rows={6}
                    variant="outlined"
                    sx={{
                      overflow: 'visible',
                      '& .MuiOutlinedInput-root': {
                        overflow: 'visible',
                        backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        overflow: 'visible',
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#4ce1b6',
                      },
                    }}
                  />
                </CardContent>
              </Card>

              {/* Check-in/out Time */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                  border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: isDark ? '#fff' : '#212121',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                    }}
                  >
                    Check-in/out Time
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.is24x7}
                        onChange={(e) => handleInputChange('is24x7', e.target.checked)}
                        sx={{
                          color: '#4ce1b6',
                          '&.Mui-checked': {
                            color: '#4ce1b6',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDark ? '#ddd' : '#212121',
                          fontWeight: 500,
                        }}
                      >
                        24x7
                      </Typography>
                    }
                    sx={{ margin: 0 }}
                  />
                </Box>
                <Divider sx={{ mb: 3, borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0' }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TimePicker
                      label="Check-in"
                      value={formData.checkInTime}
                      onChange={(newValue) => {
                        handleInputChange('checkInTime', newValue);
                        if (errors.checkInTime) {
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.checkInTime;
                            return newErrors;
                          });
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          required: true,
                          error: !!errors.checkInTime,
                          helperText: errors.checkInTime,
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccessTimeIcon sx={{ color: isDark ? '#999' : '#757575' }} />
                              </InputAdornment>
                            ),
                          },
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                              '&:hover': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#4ce1b6',
                                },
                              },
                              '&.Mui-focused': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#4ce1b6',
                                  borderWidth: '2px',
                                },
                              },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#4ce1b6',
                            },
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TimePicker
                      label="Check-out"
                      value={formData.checkOutTime}
                      onChange={(newValue) => {
                        handleInputChange('checkOutTime', newValue);
                        if (errors.checkOutTime) {
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.checkOutTime;
                            return newErrors;
                          });
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          required: true,
                          error: !!errors.checkOutTime,
                          helperText: errors.checkOutTime,
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccessTimeIcon sx={{ color: isDark ? '#999' : '#757575' }} />
                              </InputAdornment>
                            ),
                          },
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                              '&:hover': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#4ce1b6',
                                },
                              },
                              '&.Mui-focused': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#4ce1b6',
                                  borderWidth: '2px',
                                },
                              },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#4ce1b6',
                            },
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Grid>

          {/* Right Column - Google Map (height till Social Media Links label) */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                position: 'sticky',
                top: 20,
                backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#f5f5f5',
                border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
                borderRadius: 2,
                minHeight: 380,
                height: '100%',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: isDark ? '#fff' : '#212121', fontWeight: 600 }}
                >
                  Property Location
                </Typography>
                {mapOpenUrl && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => window.open(mapOpenUrl, '_blank')}
                    sx={{
                      color: '#4ce1b6',
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Open Map
                  </Button>
                )}
              </Box>
              {mapEmbedUrl ? (
                <Box sx={{ height: 360, backgroundColor: isDark ? '#1f1f24' : '#f8f8f8' }}>
                  <iframe
                    title="Hotel Address Map"
                    src={mapEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    minHeight: 360,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDark ? '#999' : '#757575',
                      textAlign: 'center',
                    }}
                  >
                    Add hotel address or latitude/longitude (below) to preview location on map.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Contacts (xs-6) and Social Media Links (xs-6) - Full Width Row */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: isDark ? '#fff' : '#212121',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                    }}
                  >
                    Contacts
                  </Typography>
                  <Divider sx={{ mb: 3, borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0' }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                      <TextField
                        label="Property Whatsapp Number"
                        value={formData.whatsappNumber}
                        onChange={(e) => {
                          handleInputChange('whatsappNumber', e.target.value);
                          if (errors.whatsappNumber) {
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.whatsappNumber;
                              return newErrors;
                            });
                          }
                        }}
                        fullWidth
                        variant="outlined"
                        error={!!errors.whatsappNumber}
                        helperText={errors.whatsappNumber}
                        inputProps={{ maxLength: 20 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <WhatsAppIcon sx={{ color: isDark ? '#999' : '#757575' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                            '&:hover': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4ce1b6',
                              },
                            },
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4ce1b6',
                                borderWidth: '2px',
                              },
                            },
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#4ce1b6',
                          },
                        }}
                      />

                      <Box>
                        <TextField
                          label="Property Email Address"
                          value={emailInput}
                          onChange={(e) => {
                            setEmailInput(e.target.value);
                            if (errors.emailInput) {
                              setErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors.emailInput;
                                return newErrors;
                              });
                            }
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddEmail();
                            }
                          }}
                          fullWidth
                          variant="outlined"
                          type="email"
                          error={!!errors.emailInput}
                          helperText={errors.emailInput}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon sx={{ color: isDark ? '#999' : '#757575' }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button
                                  variant="text"
                                  size="small"
                                  onClick={handleAddEmail}
                                  sx={{
                                    color: '#4ce1b6',
                                    minWidth: 'auto',
                                    px: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    '&:hover': {
                                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(25, 118, 210, 0.08)',
                                    },
                                  }}
                                >
                                  Add
                                </Button>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                              '&:hover': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#4ce1b6',
                                },
                              },
                              '&.Mui-focused': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#4ce1b6',
                                  borderWidth: '2px',
                                },
                              },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#4ce1b6',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {formData.emailAddresses.map((email, index) => (
                            <Chip
                              key={index}
                              label={email}
                              onDelete={() => handleRemoveEmail(email)}
                              deleteIcon={<CloseIcon />}
                              size="small"
                              sx={{
                                backgroundColor: isDark ? alpha('#ffffff', 0.06) : '#e3f2fd',
                                color: isDark ? '#ddd' : '#1976d2',
                              }}
                            />
                          ))}
                        </Box>
                      </Box>

                      <Box>
                        <TextField
                          label="Property Mobile Number"
                          value={mobileInput}
                          onChange={(e) => {
                            setMobileInput(e.target.value);
                            if (errors.mobileInput) {
                              setErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors.mobileInput;
                                return newErrors;
                              });
                            }
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddMobile();
                            }
                          }}
                          fullWidth
                          variant="outlined"
                          error={!!errors.mobileInput}
                          helperText={errors.mobileInput}
                          inputProps={{ maxLength: 20 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon sx={{ color: isDark ? '#999' : '#757575' }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button
                                  variant="text"
                                  size="small"
                                  onClick={handleAddMobile}
                                  sx={{
                                    color: '#4ce1b6',
                                    minWidth: 'auto',
                                    px: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    '&:hover': {
                                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(25, 118, 210, 0.08)',
                                    },
                                  }}
                                >
                                  Add
                                </Button>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                              '&:hover': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#4ce1b6',
                                },
                              },
                              '&.Mui-focused': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#4ce1b6',
                                  borderWidth: '2px',
                                },
                              },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#4ce1b6',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {formData.mobileNumbers.map((mobile, index) => (
                            <Chip
                              key={index}
                              label={mobile}
                              onDelete={() => handleRemoveMobile(mobile)}
                              deleteIcon={<CloseIcon />}
                              size="small"
                              sx={{
                                backgroundColor: isDark ? alpha('#ffffff', 0.06) : '#e3f2fd',
                                color: isDark ? '#ddd' : '#1976d2',
                              }}
                            />
                          ))}
                        </Box>
                      </Box>

                      <TextField
                        label="Property Landline Number"
                        value={formData.landlineNumber}
                        onChange={(e) => {
                          handleInputChange('landlineNumber', e.target.value);
                          if (errors.landlineNumber) {
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.landlineNumber;
                              return newErrors;
                            });
                          }
                        }}
                        fullWidth
                        variant="outlined"
                        error={!!errors.landlineNumber}
                        helperText={errors.landlineNumber}
                        inputProps={{ maxLength: 20 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon sx={{ color: isDark ? '#999' : '#757575' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                            '&:hover': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4ce1b6',
                              },
                            },
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4ce1b6',
                                borderWidth: '2px',
                              },
                            },
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#4ce1b6',
                          },
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>

                {/* Social Media Links (xs-6) */}
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                      border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        color: isDark ? '#fff' : '#212121',
                        fontWeight: 600,
                        fontSize: '1.1rem',
                      }}
                    >
                      Social Media Links
                    </Typography>
                    <Divider sx={{ mb: 3, borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0' }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                      <TextField
                        label="Facebook profile link"
                        value={formData.facebookLink}
                        onChange={(e) => {
                          handleInputChange('facebookLink', e.target.value);
                          if (errors.facebookLink) {
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.facebookLink;
                              return newErrors;
                            });
                          }
                        }}
                        fullWidth
                        variant="outlined"
                        type="url"
                        error={!!errors.facebookLink}
                        helperText={errors.facebookLink}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FacebookIcon sx={{ color: isDark ? '#999' : '#757575' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                            '&:hover': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4ce1b6',
                              },
                            },
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4ce1b6',
                                borderWidth: '2px',
                              },
                            },
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#4ce1b6',
                          },
                        }}
                      />
                      <TextField
                        label="LinkedIn profile link"
                        value={formData.linkedinLink}
                        onChange={(e) => {
                          handleInputChange('linkedinLink', e.target.value);
                          if (errors.linkedinLink) {
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.linkedinLink;
                              return newErrors;
                            });
                          }
                        }}
                        fullWidth
                        variant="outlined"
                        type="url"
                        error={!!errors.linkedinLink}
                        helperText={errors.linkedinLink}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LinkedInIcon sx={{ color: isDark ? '#999' : '#757575' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                            '&:hover': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4ce1b6',
                              },
                            },
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4ce1b6',
                                borderWidth: '2px',
                              },
                            },
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#4ce1b6',
                          },
                        }}
                      />
                      <TextField
                        label="Twitter profile link"
                        value={formData.twitterLink}
                        onChange={(e) => {
                          handleInputChange('twitterLink', e.target.value);
                          if (errors.twitterLink) {
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.twitterLink;
                              return newErrors;
                            });
                          }
                        }}
                        fullWidth
                        variant="outlined"
                        type="url"
                        error={!!errors.twitterLink}
                        helperText={errors.twitterLink}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <TwitterIcon sx={{ color: isDark ? '#999' : '#757575' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                            '&:hover': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4ce1b6',
                              },
                            },
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4ce1b6',
                                borderWidth: '2px',
                              },
                            },
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#4ce1b6',
                          },
                        }}
                      />
                      <TextField
                        label="Instagram profile link"
                        value={formData.instagramLink}
                        onChange={(e) => {
                          handleInputChange('instagramLink', e.target.value);
                          if (errors.instagramLink) {
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.instagramLink;
                              return newErrors;
                            });
                          }
                        }}
                        fullWidth
                        variant="outlined"
                        type="url"
                        error={!!errors.instagramLink}
                        helperText={errors.instagramLink}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <InstagramIcon sx={{ color: isDark ? '#999' : '#757575' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                            '&:hover': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4ce1b6',
                              },
                            },
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4ce1b6',
                                borderWidth: '2px',
                              },
                            },
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#4ce1b6',
                          },
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
          </Grid>

          {/* Meta Search Links - Same Row (xs-6 each) */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: isDark ? '#fff' : '#212121',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                }}
              >
                Meta Search Links
              </Typography>
              <Divider sx={{ mb: 3, borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0' }} />
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Provide your Property Tripadvisor review link."
                    value={formData.tripadvisorLink}
                    onChange={(e) => {
                      handleInputChange('tripadvisorLink', e.target.value);
                      if (errors.tripadvisorLink) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.tripadvisorLink;
                          return newErrors;
                        });
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    type="url"
                    error={!!errors.tripadvisorLink}
                    helperText={errors.tripadvisorLink}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TripOriginIcon sx={{ color: isDark ? '#999' : '#757575' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#4ce1b6',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Google search link"
                    value={formData.googleLink}
                    onChange={(e) => {
                      handleInputChange('googleLink', e.target.value);
                      if (errors.googleLink) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.googleLink;
                          return newErrors;
                        });
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    type="url"
                    error={!!errors.googleLink}
                    helperText={errors.googleLink}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <GoogleIcon sx={{ color: isDark ? '#999' : '#757575' }} />
                        </InputAdornment>
                      ),
                      endAdornment: formData.googleLink && (
                        <InputAdornment position="end">
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => window.open(formData.googleLink, '_blank')}
                            sx={{
                              color: '#4ce1b6',
                              textTransform: 'none',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(25, 118, 210, 0.08)',
                              },
                            }}
                          >
                            View Link
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#4ce1b6',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Hotel Address */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: isDark ? '#fff' : '#212121',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                }}
              >
                Hotel Address
              </Typography>
              <Divider sx={{ mb: 3, borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0' }} />
              <TextField
                label="Update hotel address"
                value={formData.address}
                onChange={(e) => {
                  handleInputChange('address', e.target.value);
                  if (errors.address) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.address;
                      return newErrors;
                    });
                  }
                }}
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
                error={!!errors.address}
                helperText={errors.address || 'Map preview updates based on this address.'}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4ce1b6',
                  },
                }}
              />
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="City"
                    value={formData.city}
                    onChange={(e) => {
                      handleInputChange('city', e.target.value);
                      if (errors.city) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.city;
                          return newErrors;
                        });
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    error={!!errors.city}
                    helperText={errors.city}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#4ce1b6',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Pincode"
                    value={formData.pincode}
                    onChange={(e) => {
                      handleInputChange('pincode', e.target.value);
                      if (errors.pincode) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.pincode;
                          return newErrors;
                        });
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    error={!!errors.pincode}
                    helperText={errors.pincode}
                    inputProps={{ maxLength: 20 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#4ce1b6',
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  mb: 1,
                  color: isDark ? '#aaa' : '#616161',
                  fontWeight: 600,
                }}
              >
                Map coordinates (optional)
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: isDark ? '#888' : '#757575' }}>
                Decimal degrees (WGS84). Used for dynamic pricing maps and distance. Leave both blank to clear saved
                coordinates. Latitude −90 to 90, longitude −180 to 180.
              </Typography>
              <HotelLatLngAssist
                apiKey={typeof window !== 'undefined' ? window.DJANGO_CONTEXT?.googleMapsApiKey || '' : ''}
                addressParts={{
                  name: formData.propertyName,
                  address: formData.address,
                  city: formData.city,
                  pincode: formData.pincode,
                  country:
                    typeof window !== 'undefined' ? window.DJANGO_CONTEXT?.selectedHotel?.country || '' : '',
                }}
                latitude={formData.latitude}
                longitude={formData.longitude}
                isDark={isDark}
                onApplyCoords={(latStr, lngStr) => {
                  handleInputChange('latitude', latStr);
                  handleInputChange('longitude', lngStr);
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next.latitude;
                    delete next.longitude;
                    return next;
                  });
                }}
                onNotify={pushPageToast}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Latitude"
                    value={formData.latitude}
                    onChange={(e) => {
                      handleInputChange('latitude', e.target.value);
                      if (errors.latitude) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.latitude;
                          return next;
                        });
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    placeholder="e.g. 28.6139"
                    inputProps={{ inputMode: 'decimal' }}
                    error={!!errors.latitude}
                    helperText={errors.latitude || ''}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#4ce1b6',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Longitude"
                    value={formData.longitude}
                    onChange={(e) => {
                      handleInputChange('longitude', e.target.value);
                      if (errors.longitude) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.longitude;
                          return next;
                        });
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    placeholder="e.g. 77.2090"
                    inputProps={{ inputMode: 'decimal' }}
                    error={!!errors.longitude}
                    helperText={errors.longitude || ''}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4ce1b6',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#4ce1b6',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Action Buttons - Full Width */}
          <Grid item xs={12}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              justifyContent: { sm: 'flex-end' },
              gap: 2,
              mt: 3,
              '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' } },
            }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                size="large"
                sx={{
                  color: isDark ? '#ff4861' : '#d32f2f',
                  borderColor: isDark ? '#ff4861' : '#d32f2f',
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: isDark ? '#ff4861' : '#d32f2f',
                    borderWidth: '2px',
                    backgroundColor: isDark ? 'rgba(255, 72, 97, 0.1)' : 'rgba(211, 47, 47, 0.08)',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                size="large"
                disabled={saving}
                sx={{
                  backgroundColor: '#4ce1b6',
                  color: isDark ? '#212121' : '#ffffff',
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: isDark
                    ? '0 4px 14px 0 rgba(76, 225, 182, 0.39)'
                    : '0 4px 14px 0 rgba(25, 118, 210, 0.39)',
                  '&:hover': {
                    backgroundColor: isDark ? '#3dd1a3' : '#1565c0',
                    boxShadow: isDark
                      ? '0 6px 20px 0 rgba(76, 225, 182, 0.5)'
                      : '0 6px 20px 0 rgba(25, 118, 210, 0.5)',
                  },
                  '&:disabled': {
                    backgroundColor: isDark ? '#666' : '#ccc',
                    color: isDark ? '#999' : '#666',
                  },
                }}
              >
                {saving ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: isDark ? '#212121' : '#ffffff' }} />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default BasicDetailsPage;

